/*
==============================================================================
Código hecho por Rcrea
Archivo: assets/section-rcrea-guten-bento-grid.js
Sección: Güten Brew | Bento Grid Premium
Función:
- Degradado de texto al scroll
- Reveal de cards
- Reproducción robusta video desktop/mobile
- Compatibilidad editor Shopify
- Pausa videos de cards ocultas sólo en mobile
==============================================================================
*/

(() => {
  const sectionSelector = "[data-gb-bento]";
  const initializedAttribute = "data-gb-bento-initialized";
  const mobileQuery = window.matchMedia("(max-width: 749px)");

  const clamp = (value, min, max) => {
    return Math.min(Math.max(value, min), max);
  };

  const addCleanup = (section, callback) => {
    if (!Array.isArray(section._gbBentoCleanups)) {
      section._gbBentoCleanups = [];
    }

    section._gbBentoCleanups.push(callback);
  };

  const updateSectionProgress = (section) => {
    const rect = section.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight ||
      document.documentElement.clientHeight;

    const total = viewportHeight + rect.height;
    const passed = viewportHeight - rect.top;
    const progress = clamp(passed / total, 0, 1);

    section.style.setProperty(
      "--gb-bento-scroll",
      progress.toFixed(4)
    );
  };

  const initScrollGradient = (section) => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
      section.style.setProperty("--gb-bento-scroll", "0.5");
      return;
    }

    let ticking = false;

    const requestUpdate = () => {
      if (ticking) return;

      ticking = true;

      window.requestAnimationFrame(() => {
        updateSectionProgress(section);
        ticking = false;
      });
    };

    updateSectionProgress(section);

    window.addEventListener("scroll", requestUpdate, {
      passive: true
    });

    window.addEventListener("resize", requestUpdate, {
      passive: true
    });

    addCleanup(section, () => {
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
    });
  };

  const initCardsReveal = (section) => {
    const cards = Array.from(
      section.querySelectorAll(".gb-bento__card")
    );

    if (!cards.length) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (
      prefersReducedMotion ||
      !("IntersectionObserver" in window)
    ) {
      cards.forEach((card) => {
        card.classList.add("is-visible");
      });

      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      {
        root: null,
        threshold: 0.12,
        rootMargin: "0px 0px -5% 0px"
      }
    );

    cards.forEach((card, index) => {
      card.style.transitionDelay =
        `${Math.min(index * 70, 420)}ms`;

      observer.observe(card);
    });

    addCleanup(section, () => {
      observer.disconnect();
    });
  };

  const prepareVideo = (video) => {
    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.loop = true;

    video.setAttribute("muted", "");
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");
    video.setAttribute("loop", "");
    video.setAttribute("autoplay", "");

    /*
    Evita que reglas externas del theme oculten el video
    o modifiquen accidentalmente sus dimensiones.
    */
    video.style.opacity = "1";
    video.style.visibility = "visible";
  };

  const playVideo = (video) => {
    if (!video) return;

    prepareVideo(video);

    const playPromise = video.play();

    if (
      playPromise &&
      typeof playPromise.catch === "function"
    ) {
      playPromise.catch(() => {
        /*
        Segundo intento después de asegurar muted.
        Algunos navegadores bloquean el primer play()
        durante reconstrucciones del editor Shopify.
        */

        video.muted = true;

        window.setTimeout(() => {
          const retryPromise = video.play();

          if (
            retryPromise &&
            typeof retryPromise.catch === "function"
          ) {
            retryPromise.catch(() => {});
          }
        }, 120);
      });
    }
  };

  const pauseVideo = (video) => {
    if (!video) return;

    try {
      video.pause();
    } catch (error) {}
  };

  const getCardMedia = (card) => {
    return {
      desktopWrapper: card.querySelector(
        ".gb-bento__card-media--desktop"
      ),
      mobileWrapper: card.querySelector(
        ".gb-bento__card-media--mobile"
      )
    };
  };

  const getVideosFromWrapper = (wrapper) => {
    if (!wrapper) return [];

    return Array.from(
      wrapper.querySelectorAll("video")
    );
  };

  const pauseAllCardVideos = (card) => {
    if (!card) return;

    card.querySelectorAll("video").forEach((video) => {
      pauseVideo(video);
    });
  };

  const syncCardVideo = (card) => {
    if (!card) return;

    if (
      mobileQuery.matches &&
      card.classList.contains("gb-bento__card--hide-mobile")
    ) {
      pauseAllCardVideos(card);
      return;
    }

    const {
      desktopWrapper,
      mobileWrapper
    } = getCardMedia(card);

    const desktopVideos =
      getVideosFromWrapper(desktopWrapper);

    const mobileVideos =
      getVideosFromWrapper(mobileWrapper);

    const hasMobileMedia =
      card.classList.contains(
        "gb-bento__card--has-mobile-media"
      );

    const isMobile = mobileQuery.matches;

    /*
    MOBILE
    ==============================================================
    Si existe media exclusivo mobile:
    - pausa desktop
    - reproduce mobile

    Si NO existe media exclusivo mobile:
    - reutiliza correctamente desktop
    ==============================================================
    */

    if (isMobile) {
      if (hasMobileMedia && mobileWrapper) {
        desktopVideos.forEach(pauseVideo);
        mobileVideos.forEach(playVideo);
      } else {
        mobileVideos.forEach(pauseVideo);
        desktopVideos.forEach(playVideo);
      }

      return;
    }

    /*
    DESKTOP
    ==============================================================
    Siempre reproduce media desktop.
    El media mobile permanece pausado.
    ==============================================================
    */

    mobileVideos.forEach(pauseVideo);
    desktopVideos.forEach(playVideo);
  };

  const syncAllVideos = (section) => {
    if (!section) return;

    const cards = Array.from(
      section.querySelectorAll(".gb-bento__card")
    );

    cards.forEach((card) => {
      syncCardVideo(card);
    });
  };

  const initVideoVisibilityObserver = (section) => {
    if (!("IntersectionObserver" in window)) {
      syncAllVideos(section);
      return;
    }

    const cards = Array.from(
      section.querySelectorAll(".gb-bento__card")
    );

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const card = entry.target;

          if (entry.isIntersecting) {
            syncCardVideo(card);
            return;
          }

          /*
          Pausa videos completamente fuera de viewport
          para ahorrar recursos.
          */

          card.querySelectorAll("video").forEach((video) => {
            pauseVideo(video);
          });
        });
      },
      {
        threshold: 0.01,
        rootMargin: "150px 0px 150px 0px"
      }
    );

    cards.forEach((card) => {
      observer.observe(card);
    });

    addCleanup(section, () => {
      observer.disconnect();
    });
  };

  const initVideos = (section) => {
    let resizeTimer = null;

    /*
    Prepara todos los videos inmediatamente.
    */

    section.querySelectorAll("video").forEach((video) => {
      prepareVideo(video);

      video.addEventListener(
        "loadedmetadata",
        () => {
          const card = video.closest(".gb-bento__card");

          if (card) {
            syncCardVideo(card);
          }
        },
        {
          once: true
        }
      );

      video.addEventListener(
        "canplay",
        () => {
          const card = video.closest(".gb-bento__card");

          if (card) {
            syncCardVideo(card);
          }
        },
        {
          once: true
        }
      );
    });

    const refreshVideos = () => {
      window.clearTimeout(resizeTimer);

      resizeTimer = window.setTimeout(() => {
        syncAllVideos(section);
      }, 80);
    };

    /*
    Primer inicio.
    Usamos dos frames para que Shopify haya terminado
    de calcular display/visibility del media.
    */

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        syncAllVideos(section);
      });
    });

    /*
    Segundo intento unos milisegundos después.
    Importante dentro del editor Shopify.
    */

    window.setTimeout(() => {
      syncAllVideos(section);
    }, 350);

    initVideoVisibilityObserver(section);

    if (
      typeof mobileQuery.addEventListener === "function"
    ) {
      mobileQuery.addEventListener(
        "change",
        refreshVideos
      );

      addCleanup(section, () => {
        mobileQuery.removeEventListener(
          "change",
          refreshVideos
        );
      });
    } else if (
      typeof mobileQuery.addListener === "function"
    ) {
      mobileQuery.addListener(refreshVideos);

      addCleanup(section, () => {
        mobileQuery.removeListener(refreshVideos);
      });
    }

    window.addEventListener(
      "resize",
      refreshVideos,
      {
        passive: true
      }
    );

    addCleanup(section, () => {
      window.clearTimeout(resizeTimer);

      window.removeEventListener(
        "resize",
        refreshVideos
      );

      section.querySelectorAll("video").forEach((video) => {
        pauseVideo(video);
      });
    });
  };

  const initBento = (section) => {
    if (
      !section ||
      section.hasAttribute(initializedAttribute)
    ) {
      return;
    }

    section.setAttribute(
      initializedAttribute,
      "true"
    );

    section._gbBentoCleanups = [];

    initScrollGradient(section);
    initCardsReveal(section);
    initVideos(section);
  };

  const initAllBentos = (root = document) => {
    const sections = [];

    if (
      root.matches &&
      root.matches(sectionSelector)
    ) {
      sections.push(root);
    }

    if (root.querySelectorAll) {
      sections.push(
        ...root.querySelectorAll(sectionSelector)
      );
    }

    sections.forEach((section) => {
      initBento(section);
    });
  };

  const destroyBento = (section) => {
    if (!section) return;

    if (
      Array.isArray(section._gbBentoCleanups)
    ) {
      section._gbBentoCleanups.forEach((cleanup) => {
        if (typeof cleanup === "function") {
          cleanup();
        }
      });
    }

    section
      .querySelectorAll("video")
      .forEach((video) => {
        pauseVideo(video);
      });

    section._gbBentoCleanups = [];

    section.removeAttribute(
      initializedAttribute
    );
  };

  /*
  ============================================================================
  INICIALIZACIÓN
  ============================================================================
  */

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        initAllBentos();
      }
    );
  } else {
    initAllBentos();
  }

  /*
  ============================================================================
  SHOPIFY THEME EDITOR
  ============================================================================
  */

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initAllBentos(event.target);

      window.setTimeout(() => {
        const section =
          event.target.matches?.(sectionSelector)
            ? event.target
            : event.target.querySelector?.(
                sectionSelector
              );

        if (section) {
          syncAllVideos(section);
        }
      }, 150);
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      if (
        event.target.matches &&
        event.target.matches(sectionSelector)
      ) {
        destroyBento(event.target);
      }

      event.target
        .querySelectorAll?.(sectionSelector)
        .forEach((section) => {
          destroyBento(section);
        });
    }
  );

  document.addEventListener(
    "shopify:block:select",
    (event) => {
      const section =
        event.target.closest?.(sectionSelector);

      if (!section) return;

      /*
      Al seleccionar la card en el editor,
      reactivamos inmediatamente su video.
      */

      const card =
        event.target.closest?.(".gb-bento__card");

      if (card) {
        window.setTimeout(() => {
          syncCardVideo(card);
        }, 50);
      } else {
        syncAllVideos(section);
      }
    }
  );

  document.addEventListener(
    "shopify:block:load",
    (event) => {
      const section =
        event.target.closest?.(sectionSelector);

      if (!section) return;

      window.setTimeout(() => {
        syncAllVideos(section);
      }, 120);
    }
  );
})();